<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';

import { X, BellOff, Trash2 } from 'lucide-vue-next';

import { useNotifications } from '@/composables/useNotifications';
import { getNotificationTab } from '@/services/notificationSettingsService';

import NotificationItem from './NotificationItem.vue';
import NotificationQuickSettings from './NotificationQuickSettings.vue';

const {
  allNotifications,
  unreadCount,
  isDrawerOpen,
  closeDrawer,
  markAsRead,
  markAllAsRead,
  removeNotification,
  clearAll,
} = useNotifications();

const activeTab = ref('user');

const sortedNotifications = computed(() => {
  return [...allNotifications.value].sort((a, b) => {
    // Unread first, then by timestamp
    if (a.read !== b.read) {
      return a.read ? 1 : -1;
    }
    return new Date(b.timestamp) - new Date(a.timestamp);
  });
});

// Unread counts per tab, shown next to the tab labels.
const tabUnread = computed(() => {
  const counts = { system: 0, user: 0 };
  allNotifications.value.forEach((n) => {
    if (!n.read) counts[getNotificationTab(n)]++;
  });
  return counts;
});

const tabNotifications = computed(() =>
  sortedNotifications.value.filter((n) => getNotificationTab(n) === activeTab.value),
);

const hasNotifications = computed(() => allNotifications.value.length > 0);

// Prevent body scroll while the drawer is open. The active tab defaults to
// 'user' and otherwise keeps whatever the user last selected.
watch(isDrawerOpen, (isOpen) => {
  document.body.style.overflow = isOpen ? 'hidden' : '';
});

// Handle escape key to close drawer
const handleKeyDown = (event) => {
  if (event.key === 'Escape' && isDrawerOpen.value) {
    closeDrawer();
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown);
  document.body.style.overflow = '';
});

const handleNotificationClick = (notification) => {
  if (notification.data?.url) {
    closeDrawer();
  }
};
</script>

<template>
  <teleport to="body">
    <transition name="drawer">
      <div v-if="isDrawerOpen" class="notification-drawer-wrapper">
        <div class="drawer-backdrop" @click="closeDrawer" />
        <div class="notification-drawer">
          <div class="drawer-header">
            <div class="header-title-section">
              <h2 class="drawer-title">
                Notifications
              </h2>
              <span v-if="unreadCount > 0" class="unread-badge">{{ unreadCount }}</span>
            </div>
            <div class="header-actions">
              <button
                v-if="unreadCount > 0"
                class="action-link"
                @click="markAllAsRead"
              >
                Mark all read
              </button>
              <NotificationQuickSettings />
              <button class="close-btn" @click="closeDrawer">
                <X :size="20" />
              </button>
            </div>
          </div>

          <el-tabs v-model="activeTab" class="notification-tabs">
            <el-tab-pane name="user">
              <template #label>
                <span class="tab-label">
                  User
                  <span v-if="tabUnread.user > 0" class="tab-count">{{ tabUnread.user }}</span>
                </span>
              </template>
            </el-tab-pane>
            <el-tab-pane name="system">
              <template #label>
                <span class="tab-label">
                  System
                  <span v-if="tabUnread.system > 0" class="tab-count">{{ tabUnread.system }}</span>
                </span>
              </template>
            </el-tab-pane>
          </el-tabs>

          <div class="drawer-content">
            <div v-if="tabNotifications.length > 0" class="notifications-list">
              <NotificationItem
                v-for="notification in tabNotifications"
                :key="notification.id"
                :notification="notification"
                @mark-read="markAsRead"
                @remove="removeNotification"
                @click="handleNotificationClick"
              />
            </div>

            <div v-else class="empty-state">
              <div class="empty-icon">
                <BellOff :size="48" :stroke-width="1.5" />
              </div>
              <h3 class="empty-title">
                No notifications
              </h3>
              <p class="empty-description">
                You're all caught up! New {{ activeTab }} notifications will appear here.
              </p>
            </div>
          </div>

          <div v-if="hasNotifications" class="drawer-footer">
            <button class="clear-all-btn" @click="clearAll">
              <Trash2 :size="16" />
              Clear all notifications
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<style lang="scss" scoped>
.notification-drawer-wrapper {
  position: fixed;
  inset: 0;
  z-index: 1000;
}

.drawer-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
}

.notification-drawer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: 420px;
  background: var(--wp-surface-default);
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--wp-border-primary);
  flex-shrink: 0;
}

.header-title-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.drawer-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--wp-text-primary);
}

.unread-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: var(--wp-state-main);
  color: var(--wp-constant-white);
  font-size: 12px;
  font-weight: 600;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-link {
  padding: 6px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--wp-text-accent);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast);

  &:hover {
    background: var(--wp-surface-hover);
  }
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--wp-text-tertiary);
  cursor: pointer;
  transition: all var(--transition-fast);

  &:hover {
    background: var(--wp-surface-hover);
    color: var(--wp-text-primary);
  }
}

.notification-tabs {
  flex-shrink: 0;
  padding: 0 16px;

  // The list is rendered below the tab bar, so hide EP's own (empty) pane area.
  :deep(.el-tabs__content) {
    display: none;
  }

  :deep(.el-tabs__header) {
    margin: 0;
  }

  :deep(.el-tabs__nav-wrap::after) {
    height: 1px;
    background-color: var(--wp-border-primary);
  }

  :deep(.el-tabs__item) {
    color: var(--wp-text-tertiary);
    font-size: 13px;
    font-weight: 500;

    &.is-active {
      color: var(--wp-text-accent);
    }
  }

  :deep(.el-tabs__active-bar) {
    background-color: var(--wp-state-main);
  }
}

.tab-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 5px;
  border-radius: 8px;
  background: var(--wp-state-main);
  color: var(--wp-constant-white);
  font-size: 10px;
  font-weight: 600;
}

.drawer-content {
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.notifications-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 20px;
  text-align: center;
}

.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--wp-surface-hover);
  color: var(--wp-text-tertiary);
  margin-bottom: 16px;
}

.empty-title {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--wp-text-primary);
}

.empty-description {
  margin: 0;
  font-size: 14px;
  color: var(--wp-text-tertiary);
  max-width: 250px;
  text-transform: capitalize;
}

.drawer-footer {
  padding: 16px 20px;
  border-top: 1px solid var(--wp-border-primary);
  flex-shrink: 0;
}

.clear-all-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background: var(--wp-surface-hover);
  color: var(--wp-text-tertiary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast);

  &:hover {
    background: var(--wp-surface-bg-icon);
    color: var(--wp-red);
  }
}

// Drawer transition
.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.3s ease;

  .notification-drawer {
    transition: transform 0.3s ease;
  }
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;

  .notification-drawer {
    transform: translateX(100%);
  }
}
</style>
